// @flow
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import update from 'immutability-helper';
import {
  endpoints,
  getDealsApi,
  getFeaturedProductsApi,
  getPopularListProductsApi,
  getRecentListProductsApi,
  getStoresDealsApi,
} from './api';

import type { Deal, WishList } from '../../entities';
import type { HTTPError } from '../../error';

export type Product = {
  +type: string;
  +name: string;
  +price: string;
  +url: string;
  +image: string;
  +originType?: string;
};

export type SpecialProductsAction = {
  +type: string;
  +deals?: Deal[];
  +featuredProducts?: Deal[];
  +products?: Product[];
  +nameSlug?: string;
  +userId?: string;
  +storesDeals?: WishList;
  +error?: HTTPError;
};

export type SpecialProductsState = {
  +isFetchingRecentProducts?: boolean;
  +recentProducts?: Product[];
  +fetchRecentProductsError?: HTTPError;
  +isFetchingPopularProducts?: boolean;
  +popularProducts?: Product[];
  +fetchPopularProductsError?: HTTPError;
  +isFetchingDeals?: boolean;
  +deals?: Deal[];
  +fetchDealsError?: HTTPError;
  +isFetchingFeaturedProducts?: boolean;
  +featuredProducts?: Deal[];
  +fetchFeaturedProductsError?: HTTPError;
  +isFetchingStoresDeals?: boolean;
  +storesDeals?: WishList[];
  +fetchStoresDealsError?: HTTPError;
};

const SPECIAL_PRODUCTS_RESET_STATE = 'SPECIAL_PRODUCTS_RESET_STATE';
const SPECIAL_PRODUCTS_FETCH_RECENT_LIST_PRODUCTS_REQUEST = 'SPECIAL_PRODUCTS_FETCH_RECENT_LIST_PRODUCTS_REQUEST';
const SPECIAL_PRODUCTS_FETCH_RECENT_LIST_PRODUCTS_SUCCESS = 'SPECIAL_PRODUCTS_FETCH_RECENT_LIST_PRODUCTS_SUCCESS';
const SPECIAL_PRODUCTS_FETCH_RECENT_LIST_PRODUCTS_ERROR = 'SPECIAL_PRODUCTS_FETCH_RECENT_LIST_PRODUCTS_ERROR';
const SPECIAL_PRODUCTS_FETCH_POPULAR_LIST_PRODUCTS_REQUEST = 'SPECIAL_PRODUCTS_FETCH_POPULAR_LIST_PRODUCTS_REQUEST';
const SPECIAL_PRODUCTS_FETCH_POPULAR_LIST_PRODUCTS_SUCCESS = 'SPECIAL_PRODUCTS_FETCH_POPULAR_LIST_PRODUCTS_SUCCESS';
const SPECIAL_PRODUCTS_FETCH_POPULAR_LIST_PRODUCTS_ERROR = 'SPECIAL_PRODUCTS_FETCH_POPULAR_LIST_PRODUCTS_ERROR';
const SPECIAL_PRODUCTS_FETCH_DEALS_REQUEST = 'SPECIAL_PRODUCTS_FETCH_DEALS_REQUEST';
const SPECIAL_PRODUCTS_FETCH_DEALS_SUCCESS = 'SPECIAL_PRODUCTS_FETCH_DEALS_SUCCESS';
const SPECIAL_PRODUCTS_FETCH_DEALS_ERROR = 'SPECIAL_PRODUCTS_FETCH_DEALS_ERROR';
const SPECIAL_PRODUCTS_FETCH_FEATURED_PRODUCTS_REQUEST = 'SPECIAL_PRODUCTS_FETCH_FEATURED_PRODUCTS_REQUEST';
const SPECIAL_PRODUCTS_FETCH_FEATURED_PRODUCTS_SUCCESS = 'SPECIAL_PRODUCTS_FETCH_FEATURED_PRODUCTS_SUCCESS';
const SPECIAL_PRODUCTS_FETCH_FEATURED_PRODUCTS_ERROR = 'SPECIAL_PRODUCTS_FETCH_FEATURED_PRODUCTS_ERROR';
const SPECIAL_PRODUCTS_FETCH_STORES_DEALS_REQUEST = 'SPECIAL_PRODUCTS_FETCH_STORES_DEALS_REQUEST';
const SPECIAL_PRODUCTS_FETCH_STORES_DEALS_SUCCESS = 'SPECIAL_PRODUCTS_FETCH_STORES_DEALS_SUCCESS';
const SPECIAL_PRODUCTS_FETCH_STORES_DEALS_ERROR = 'SPECIAL_PRODUCTS_FETCH_STORES_DEALS_ERROR';

export function fetchRecentListProducts(): SpecialProductsAction {
  return {
    type: SPECIAL_PRODUCTS_FETCH_RECENT_LIST_PRODUCTS_REQUEST,
  }
}

export function fetchRecentListProductsSuccess(products: Product[]): SpecialProductsAction {
  return {
    type: SPECIAL_PRODUCTS_FETCH_RECENT_LIST_PRODUCTS_SUCCESS,
    products,
  }
}

export function fetchRecentListProductsError(error: HTTPError): SpecialProductsAction {
  return {
    type: SPECIAL_PRODUCTS_FETCH_RECENT_LIST_PRODUCTS_ERROR,
    error,
  }
}

export function fetchPopularListProducts(): SpecialProductsAction {
  return {
    type: SPECIAL_PRODUCTS_FETCH_POPULAR_LIST_PRODUCTS_REQUEST,
  }
}

export function fetchPopularListProductsSuccess(products: Product[]): SpecialProductsAction {
  return {
    type: SPECIAL_PRODUCTS_FETCH_POPULAR_LIST_PRODUCTS_SUCCESS,
    products,
  }
}

export function fetchPopularListProductsError(error: HTTPError): SpecialProductsAction {
  return {
    type: SPECIAL_PRODUCTS_FETCH_POPULAR_LIST_PRODUCTS_ERROR,
    error,
  }
}

export function fetchDeals(): SpecialProductsAction {
  return {
    type: SPECIAL_PRODUCTS_FETCH_DEALS_REQUEST,
  };
}

export function fetchDealsSuccess(deals: Deal[]): SpecialProductsAction {
  return {
    type: SPECIAL_PRODUCTS_FETCH_DEALS_SUCCESS,
    deals,
  };
}

export function fetchDealsErrorFn(error: HTTPError): SpecialProductsAction {
  return {
    type: SPECIAL_PRODUCTS_FETCH_DEALS_ERROR,
    error,
  };
}

export function fetchFeaturedProducts(): SpecialProductsAction {
  return {
    type: SPECIAL_PRODUCTS_FETCH_FEATURED_PRODUCTS_REQUEST,
  };
}

export function fetchFeaturedProductsSuccess(featuredProducts: Deal[]): SpecialProductsAction {
  return {
    type: SPECIAL_PRODUCTS_FETCH_FEATURED_PRODUCTS_SUCCESS,
    featuredProducts,
  };
}

export function fetchFeaturedProductsErrorFn(error: HTTPError): SpecialProductsAction {
  return {
    type: SPECIAL_PRODUCTS_FETCH_FEATURED_PRODUCTS_ERROR,
    error,
  };
}

export function fetchStoresDeals(nameSlug: string, userId: string): SpecialProductsAction {
  return {
    type: SPECIAL_PRODUCTS_FETCH_STORES_DEALS_REQUEST,
    nameSlug,
    userId,
  };
}

export function fetchStoresDealsSuccess(storesDeals: WishList): SpecialProductsAction {
  return {
    type: SPECIAL_PRODUCTS_FETCH_STORES_DEALS_SUCCESS,
    storesDeals,
  };
}

export function fetchStoresDealsErrorFn(error: HTTPError): SpecialProductsAction {
  return {
    type: SPECIAL_PRODUCTS_FETCH_STORES_DEALS_ERROR,
    error,
  };
}

export function resetState(error: HTTPError): SpecialProductsAction {
  return {
    type: SPECIAL_PRODUCTS_RESET_STATE,
    error,
  };
}

export const actions = {
  SPECIAL_PRODUCTS_RESET_STATE,
  SPECIAL_PRODUCTS_FETCH_RECENT_LIST_PRODUCTS_REQUEST,
  SPECIAL_PRODUCTS_FETCH_RECENT_LIST_PRODUCTS_SUCCESS,
  SPECIAL_PRODUCTS_FETCH_RECENT_LIST_PRODUCTS_ERROR,
  SPECIAL_PRODUCTS_FETCH_POPULAR_LIST_PRODUCTS_REQUEST,
  SPECIAL_PRODUCTS_FETCH_POPULAR_LIST_PRODUCTS_SUCCESS,
  SPECIAL_PRODUCTS_FETCH_POPULAR_LIST_PRODUCTS_ERROR,
  SPECIAL_PRODUCTS_FETCH_DEALS_REQUEST,
  SPECIAL_PRODUCTS_FETCH_DEALS_SUCCESS,
  SPECIAL_PRODUCTS_FETCH_DEALS_ERROR,
  SPECIAL_PRODUCTS_FETCH_FEATURED_PRODUCTS_REQUEST,
  SPECIAL_PRODUCTS_FETCH_FEATURED_PRODUCTS_SUCCESS,
  SPECIAL_PRODUCTS_FETCH_FEATURED_PRODUCTS_ERROR,
  SPECIAL_PRODUCTS_FETCH_STORES_DEALS_REQUEST,
  SPECIAL_PRODUCTS_FETCH_STORES_DEALS_SUCCESS,
  SPECIAL_PRODUCTS_FETCH_STORES_DEALS_ERROR,
  fetchRecentListProducts,
  fetchRecentListProductsSuccess,
  fetchRecentListProductsError,
  fetchPopularListProducts,
  fetchPopularListProductsSuccess,
  fetchPopularListProductsError,
  fetchDeals,
  fetchDealsSuccess,
  fetchDealsErrorFn,
  fetchFeaturedProducts,
  fetchFeaturedProductsSuccess,
  fetchFeaturedProductsErrorFn,
  fetchStoresDeals,
  fetchStoresDealsSuccess,
  fetchStoresDealsErrorFn,
  resetState,
};

export const initialState: SpecialProductsState = {};

export default function reducer(state: SpecialProductsState = initialState,
                                action: SpecialProductsAction): SpecialProductsState {
  switch (action.type) {
    case SPECIAL_PRODUCTS_FETCH_RECENT_LIST_PRODUCTS_REQUEST:
      return update(state, {
        isFetchingRecentProducts: { $set: true },
      });
    case SPECIAL_PRODUCTS_FETCH_RECENT_LIST_PRODUCTS_SUCCESS:
      return update(state, {
        $unset: ['isFetchingRecentProducts'],
        recentProducts: { $set: action.products },
      });
    case SPECIAL_PRODUCTS_FETCH_RECENT_LIST_PRODUCTS_ERROR:
      return update(state, {
        fetchRecentProductsError: { $set: action.error },
      });
    case SPECIAL_PRODUCTS_FETCH_POPULAR_LIST_PRODUCTS_REQUEST:
      return update(state, {
        isFetchingPopularProducts: { $set: true },
      });
    case SPECIAL_PRODUCTS_FETCH_POPULAR_LIST_PRODUCTS_SUCCESS:
      return update(state, {
        $unset: ['isFetchingPopularProducts'],
        popularProducts: { $set: action.products },
      });
    case SPECIAL_PRODUCTS_FETCH_POPULAR_LIST_PRODUCTS_ERROR:
      return update(state, {
        fetchPopularProductsError: { $set: action.error },
      });
    case SPECIAL_PRODUCTS_FETCH_DEALS_REQUEST:
      return update(state, {
        isFetchingDeals: { $set: true },
      });
    case SPECIAL_PRODUCTS_FETCH_DEALS_SUCCESS:
      return update(state, {
        $unset: ['isFetchingDeals'],
        deals: { $set: action.deals },
      });
    case SPECIAL_PRODUCTS_FETCH_DEALS_ERROR:
      return update(state, {
        fetchDealsError: { $set: action.error },
      });
    case SPECIAL_PRODUCTS_FETCH_FEATURED_PRODUCTS_REQUEST:
      return update(state, {
        isFetchingFeaturedProducts: { $set: true },
      });
    case SPECIAL_PRODUCTS_FETCH_FEATURED_PRODUCTS_SUCCESS:
      return update(state, {
        $unset: ['isFetchingFeaturedProducts'],
        featuredProducts: { $set: action.featuredProducts },
      });
    case SPECIAL_PRODUCTS_FETCH_FEATURED_PRODUCTS_ERROR:
      return update(state, {
        fetchFeaturedProductsError: { $set: action.error },
      });
    case SPECIAL_PRODUCTS_FETCH_STORES_DEALS_REQUEST:
      return update(state, {
        isFetchingStoresDeals: { $set: true },
      });
    case SPECIAL_PRODUCTS_FETCH_STORES_DEALS_SUCCESS:
      return update(state, {
        $unset: ['isFetchingStoresDeals'],
        storesDeals: { $set: action.storesDeals },
      });
    case SPECIAL_PRODUCTS_FETCH_STORES_DEALS_ERROR:
      return update(state, {
        fetchStoresDealsError: { $set: action.error },
      });
    case SPECIAL_PRODUCTS_RESET_STATE:
      return initialState;
    default:
      return state;
  }
}

export function fetchSpecialProductsRecentProductsEpic(action$: Observable<SpecialProductsAction>): Observable<SpecialProductsAction> {
  return action$.pipe(
    ofType(SPECIAL_PRODUCTS_FETCH_RECENT_LIST_PRODUCTS_REQUEST),
    switchMap(() => getRecentListProductsApi().pipe(
      map((products: Product[]) => fetchRecentListProductsSuccess(products)),
      catchError((e: HTTPError) => of(fetchRecentListProductsError(e)))),
    ));
}

export function fetchSpecialProductsPopularProductsEpic(action$: Observable<SpecialProductsAction>): Observable<SpecialProductsAction> {
  return action$.pipe(
    ofType(SPECIAL_PRODUCTS_FETCH_POPULAR_LIST_PRODUCTS_REQUEST),
    switchMap(() => getPopularListProductsApi({
        limit: 5,
      }).pipe(
      map((products: Product[]) => fetchPopularListProductsSuccess(products)),
      catchError((e: HTTPError) => of(fetchPopularListProductsError(e)))),
    ));
}

export function fetchSpecialProductsBestDealsEpic(action$: Observable<SpecialProductsAction>): Observable<SpecialProductsAction> {
  return action$.pipe(
    ofType(SPECIAL_PRODUCTS_FETCH_DEALS_REQUEST),
    switchMap(() => getDealsApi().pipe(
      map((deals: Deal[]) => {
        const formatDeals = deals.map((d: Deal) => ({
          type: 'product',
          name: d.name,
          price: d.priceNow,
          url: d.url,
          image: d.image,
        }));
        return fetchDealsSuccess(formatDeals);
      }),
      catchError((e: HTTPError) => of(fetchDealsErrorFn(e)))),
    ));
}

export function fetchSpecialProductsFeaturedProductsEpic(action$: Observable<SpecialProductsAction>): Observable<SpecialProductsAction> {
  return action$.pipe(
    ofType(SPECIAL_PRODUCTS_FETCH_FEATURED_PRODUCTS_REQUEST),
    switchMap(() => getFeaturedProductsApi().pipe(
      map((featuredProducts: Deal[]) => fetchFeaturedProductsSuccess(featuredProducts)),
      catchError((e: HTTPError) => of(fetchFeaturedProductsErrorFn(e))),
    )));
}

export function fetchSpecialProductsStoresDealsEpic(action$: Observable<SpecialProductsAction>): Observable<SpecialProductsAction> {
  return action$.pipe(
    ofType(SPECIAL_PRODUCTS_FETCH_STORES_DEALS_REQUEST),
    switchMap(({ nameSlug, userId }) => getStoresDealsApi({
      sort: 'UPDATED_AT',
      order: 'DESC',
    }, {
      route: endpoints.GET_STORES_DEALS.replace(':nameSlug', nameSlug).replace(':userId', userId),
    }).pipe(
      map((storesDeals: WishList) => fetchStoresDealsSuccess(storesDeals)),
      catchError((e: HTTPError) => of(fetchStoresDealsErrorFn(e))),
    )),
  );
}



// WEBPACK FOOTER //
// ./src/modules/special-products/duck.js